// AGENT-KPI-TOOL: where a KPI's newest value came from, when a digital employee's run reported it
// (`ScorecardMetric.last_report.provenance`: `outcomes.record`). One mark for every place a
// seat's metrics show — Cadence's grid, the chart's Performance lens, the DE profile's scorecard:
// an "Agent" badge whose label names the agent, the period, the run and the evidence, and (the
// DE profile) that line under the metric, with the evidence link when the agent gave one.
import { Badge } from "@companyos/ui";
import type { ScorecardMetric } from "../api/types";

type Report = NonNullable<ScorecardMetric["last_report"]>;

/** The agent's report of a metric's newest value, or null (a person's, an import's, none). */
/** A scorecard row's, or any row that may carry one (the chart's dimension rows do not). */
type MaybeReported = { last_report?: ScorecardMetric["last_report"] | unknown };

export function agentReport(m: object): Report | null {
  const r = (m as MaybeReported).last_report as Report | null | undefined;
  return r?.provenance ? r : null;
}

const shortRun = (id: string) => id.replaceAll("-", "").slice(-8);

/** "Reported by Riley for 2026-W43 (run 1a2b3c4d): “Drafted …”" */
export function agentReportText(r: Report): string {
  const p = r.provenance;
  if (!p) return "";
  const who = p.agent_name ?? "an agent";
  const how = r.source === "agent" ? "Reported" : "Reported as an event";
  return `${how} by ${who} for ${r.period_key} (run ${shortRun(p.run_id)}): “${p.evidence_text}”`;
}

export function AgentReportMark({ metric }: { metric: object }) {
  const r = agentReport(metric);
  if (!r?.provenance) return null;
  const text = agentReportText(r);
  return (
    <Badge
      variant="info"
      title={text}
      aria-label={text}
      data-slot="agent-report"
      data-source={r.source}
      data-run={r.provenance.run_id}
    >
      Agent
    </Badge>
  );
}

/** The provenance as a line under the metric (the DE profile's scorecard), with the evidence link. */
export function AgentReportLine({ metric }: { metric: object }) {
  const r = agentReport(metric);
  if (!r?.provenance) return null;
  return (
    <span
      className="mt-0.5 line-clamp-2 max-w-[48ch] whitespace-normal break-words text-xs font-normal text-muted-foreground"
      title={agentReportText(r)}
      data-slot="agent-report-line"
    >
      {agentReportText(r)}
      {r.provenance.evidence_url ? (
        <>
          {" "}
          <a
            className="text-primary underline-offset-4 hover:underline"
            href={r.provenance.evidence_url}
            target="_blank"
            rel="noreferrer noopener"
          >
            Evidence
          </a>
        </>
      ) : null}
    </span>
  );
}
